import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight, ChevronDown } from 'lucide-react';
import { navLinks, applyMenuItems } from '../../mock';
import Magnetic from './Magnetic';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState({});
  const [applyOpen, setApplyOpen] = useState(false);
  const loc = useLocation();
  const onHome = loc.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setApplyOpen(false);
  }, [loc.pathname]);

  useEffect(() => {
    if (!applyOpen) return undefined;
    const onDoc = (e) => {
      if (!e.target.closest?.('[data-apply-menu]')) setApplyOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [applyOpen]);

  const toggleSubmenu = (label) => {
    setOpenSubmenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const renderLink = (l) => {
    if (l.children) {
      const isActiveChild = l.children.some((child) => loc.pathname === child.to);
      return (
        <div className="relative group px-3 py-2">
          <button type="button" className="flex items-center gap-1 font-medium text-[13.5px]">
            <span
              className={`relative z-10 transition-colors ${isActiveChild ? 'text-[#d42b92]' : 'text-black/70 group-hover:text-[#d42b92]'
                }`}
            >
              {l.label}
            </span>
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform duration-300 group-hover:rotate-180 ${isActiveChild ? 'text-[#d42b92]' : 'text-black/50 group-hover:text-[#d42b92]'
                }`}
            />
          </button>

          <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 opacity-0 translate-y-2 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition-all duration-300">
            <div className="bg-white/95 backdrop-blur-xl border border-black/5 rounded-2xl shadow-xl overflow-hidden min-w-[240px] flex flex-col p-2">
              {l.children.map((child) => (
                <Link
                  key={child.label}
                  to={child.to}
                  className={`px-4 py-3 rounded-xl transition-colors ${loc.pathname === child.to
                      ? 'bg-[#d42b92]/10 text-[#d42b92]'
                      : 'text-black/70 hover:bg-black/5 hover:text-black'
                    }`}
                >
                  <div className="text-[13px] font-medium">{child.label}</div>
                  {child.note && (
                    <div className="text-[11px] text-black/40 mt-0.5 font-normal">{child.note}</div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (l.to.startsWith('/#')) {
      const hash = l.to.slice(2);
      const handler = (e) => {
        if (onHome) {
          e.preventDefault();
          const el = document.getElementById(hash);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }
      };
      return (
        <a
          href={onHome ? `#${hash}` : `/#${hash}`}
          onClick={handler}
          className="relative px-3 py-2 group font-medium text-[13.5px]"
        >
          <span className="relative z-10 text-black/70 group-hover:text-[#d42b92] transition-colors">
            {l.label}
          </span>
          <span className="absolute left-3 right-3 bottom-1 h-px bg-[#d42b92] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
        </a>
      );
    }
    const active = loc.pathname === l.to;
    return (
      <Link to={l.to} className="relative px-3 py-2 group font-medium text-[13.5px]">
        <span
          className={`relative z-10 transition-colors ${active ? 'text-[#d42b92]' : 'text-black/70 group-hover:text-[#d42b92]'
            }`}
        >
          {l.label}
        </span>
        <span
          className={`absolute left-3 right-3 bottom-1 h-px bg-[#d42b92] origin-left transition-transform duration-300 ${active ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
            }`}
        />
      </Link>
    );
  };

  const applyActive = applyMenuItems.some((item) => {
    const path = item.to.split('?')[0];
    return loc.pathname === path;
  });

  return (
    <header className="fixed top-0 inset-x-0 z-50 py-4">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div
          className={`flex items-center justify-between bg-white/95 backdrop-blur-xl border border-black/5 shadow-[0_10px_40px_-15px_rgba(212,43,146,0.15)] rounded-full px-6 transition-all duration-500 ${scrolled ? 'py-2' : 'py-3'
            }`}
        >
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/innovher.webp" alt="Innovher" className="h-7 sm:h-8 object-contain" />
            {/* <span className="hidden sm:inline-block text-[10px] uppercase font-bold tracking-[0.2em] px-2.5 py-0.5 rounded-full bg-[#d42b92]/10 text-[#d42b92] border border-[#d42b92]/20">
              Venture Builders
            </span> */}
          </Link>

          <ul className="hidden lg:flex items-center gap-1">
            {navLinks.map((l) => (
              <li key={l.label}>{renderLink(l)}</li>
            ))}
          </ul>

          <div className="flex items-center gap-3">
            <div className="relative hidden md:block" data-apply-menu>
              <Magnetic strength={0.25}>
                <button
                  type="button"
                  aria-expanded={applyOpen}
                  aria-haspopup="menu"
                  onClick={() => setApplyOpen((v) => !v)}
                  className={`inline-flex items-center gap-1.5 pl-5 pr-1.5 py-1.5 rounded-full bg-[#d42b92] text-white text-sm font-medium hover:bg-black transition-colors duration-300 group ${applyActive ? 'ring-2 ring-[#d42b92]/30' : ''
                    }`}
                >
                  Apply
                  <span className="w-7 h-7 rounded-full bg-white text-[#d42b92] grid place-items-center group-hover:bg-white group-hover:text-black transition">
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform duration-300 ${applyOpen ? 'rotate-180' : ''
                        }`}
                    />
                  </span>
                </button>
              </Magnetic>

              {applyOpen && (
                <div
                  role="menu"
                  className="absolute top-full right-0 pt-3 z-50"
                >
                  <div className="bg-white/95 backdrop-blur-xl border border-black/5 rounded-2xl shadow-xl overflow-hidden min-w-[240px] flex flex-col p-2">
                    {applyMenuItems.map((item) => (
                      <Link
                        key={item.label}
                        to={item.to}
                        role="menuitem"
                        onClick={() => setApplyOpen(false)}
                        className={`px-4 py-3 rounded-xl transition-colors ${(() => {
                            const path = item.to.split('?')[0];
                            return loc.pathname === path;
                          })()
                            ? 'bg-[#d42b92]/10 text-[#d42b92]'
                            : 'text-black/70 hover:bg-black/5 hover:text-black'
                          }`}
                      >
                        <div className="text-[13px] font-medium flex items-center justify-between gap-2">
                          {item.label}
                          <ArrowUpRight className="w-3.5 h-3.5 opacity-40" />
                        </div>
                        {item.note && (
                          <div className="text-[11px] text-black/40 mt-0.5 font-normal">{item.note}</div>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setOpen(!open)}
              className="lg:hidden grid place-items-center w-10 h-10 rounded-full bg-black/5 border border-black/10 text-black"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden mx-5 mt-2 rounded-3xl bg-white/95 border border-black/5 p-5 shadow-2xl backdrop-blur-xl max-h-[70vh] overflow-y-auto">
          <ul className="flex flex-col gap-1">
            {navLinks.map((l) => (
              <li key={l.label}>
                {l.children ? (
                  <div>
                    <button
                      type="button"
                      onClick={() => toggleSubmenu(l.label)}
                      className="w-full flex items-center justify-between py-2.5 text-black/85 font-medium"
                    >
                      {l.label}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform duration-300 ${openSubmenus[l.label] ? 'rotate-180 text-[#d42b92]' : ''
                          }`}
                      />
                    </button>
                    {openSubmenus[l.label] && (
                      <div className="flex flex-col gap-1 pl-4 pb-2 border-l-2 border-[#d42b92]/20 ml-2 mt-1">
                        {l.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.to}
                            className="block py-2 text-black/60 text-sm hover:text-[#d42b92]"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : l.to.startsWith('/#') ? (
                  <a
                    href={onHome ? l.to.slice(1) : l.to}
                    className="block py-2.5 text-black/85 font-medium"
                  >
                    {l.label}
                  </a>
                ) : (
                  <Link to={l.to} className="block py-2.5 text-black/85 font-medium">
                    {l.label}
                  </Link>
                )}
              </li>
            ))}
            <li className="mt-4 pt-4 border-t border-black/5 space-y-2">
              {applyMenuItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={`inline-flex items-center justify-center w-full gap-2 px-4 py-3 rounded-xl font-medium ${item.to === '/apply'
                      ? 'bg-[#d42b92] text-white'
                      : 'border border-black/10 text-black/80'
                    }`}
                >
                  {item.to === '/apply' ? `Apply to ${item.label}` : item.label}
                  <ArrowUpRight className="w-4 h-4 opacity-70" />
                </Link>
              ))}
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
